import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, FormControlLabel, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setSignedEula } from '../../store/authSlice';
import api from '../../utils/axiosConfig';

const toRomanNumeral = (num) => {
    const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
    return romanNumerals[num - 1] || num.toString();
};

const EULAModal = ({ open, onClose, onAgree }) => {
    const [agreed, setAgreed] = useState(false);
    const [eulaContent, setEulaContent] = useState(null);
    const activeUnit = useSelector(state => state.auth.activeUnit);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const fetchEulaContent = async () => {
        try {
            const response = await api.get(`/api/caregiver/eula`, {
                params: {
                    unit: activeUnit.UnitSerNum
                }
            });
            setEulaContent(response.data.EULA);
        } catch (error) {
            console.log('error');
            return;
        }
    };

    const handleAgreeChange = (event) => {
        setAgreed(event.target.checked);
    };

    const handleAgreeClick = async () => {
        if (!agreed) return;
        try {
            const body = { agreed: agreed, unit: activeUnit.UnitSerNum };
            const response = await api.post(`/api/caregiver/agreeToEULA`, body);
            dispatch(setSignedEula({ signedEula: true }))
            onAgree();
        } catch (error) {
            return;
        }
    };

    const handleCancelClick = () => {
        dispatch(logout());
        navigate('/login');
    };

    const handleClose = (event, reason) => {
        // Only allow closing if user clicks the cancel button (or calls onClose directly)
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        onClose();
    };

    useEffect(() => {
        fetchEulaContent();
    }, []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    maxHeight: '700px'
                }
            }}
        >


            <DialogTitle>End-User License Agreement</DialogTitle>
            <DialogContent dividers sx={{
                overflowY: 'auto',
                padding: '16px',
                '& .section-title': {
                    fontWeight: 'bold',
                    mt: 1,
                    mb: 0.5,
                    fontSize: '0.95rem'
                },
                '& .section-content': {
                    ml: 2,
                    mb: 1,
                    fontSize: '0.875rem'
                },
                '& .subsection-list': {
                    listStyle: 'none',
                    pl: 2,
                    m: 0
                },
                '& .subsection-item': {
                    position: 'relative',
                    pl: 3.5,  // Increased padding-left
                    mb: 0.5,
                    fontSize: '0.875rem',
                    '&::before': {
                        content: 'attr(data-letter)',
                        position: 'absolute',
                        left: 0,
                        width: '2.5em',  // Fixed width for the label
                        textAlign: 'left'
                    }
                },
                '& .nested-list': {
                    listStyle: 'none',
                    pl: 2,
                    mt: 0.5,
                    mb: 0.5
                },
                '& .nested-item': {
                    position: 'relative',
                    pl: 3,  // Increased padding-left
                    mb: 0.5,
                    fontSize: '0.875rem',
                    '&::before': {
                        content: 'attr(data-number)',
                        position: 'absolute',
                        left: 0,
                        width: '2em',  // Fixed width for the label
                        textAlign: 'left'
                    }
                }
            }}>
                {eulaContent && (
                    <>
                        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
                            {eulaContent?.introduction}
                        </Typography>
                        <Typography paragraph sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                            {eulaContent?.acceptance}
                        </Typography>
                        {eulaContent?.sections.map((section, index) => (
                            <Box key={index}>
                                <Typography className="section-title">
                                    {`${index + 1}. ${section.title}`}
                                </Typography>
                                <Typography className="section-content">
                                    {section.content}
                                </Typography>
                                {section.subsections && (
                                    <ul className="subsection-list">
                                        {section.subsections.map((subsection, subIndex) => (
                                            <li
                                                key={subIndex}
                                                className="subsection-item"
                                                data-letter={`(${String.fromCharCode(97 + subIndex)})`}
                                            >
                                                {typeof subsection === 'string' ? subsection : (
                                                    <>
                                                        {subsection.content}
                                                        {subsection.nestedItems && (
                                                            <ul className="nested-list">
                                                                {subsection.nestedItems.map((item, itemIndex) => (
                                                                    <li
                                                                        key={itemIndex}
                                                                        className="nested-item"
                                                                        data-number={`(${toRomanNumeral(itemIndex + 1)})`}
                                                                    >
                                                                        {item}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Box>
                        ))}
                        {/*  Only show the checkbox if there is eulaContent */}
                        {eulaContent && (
                            <FormControlLabel
                                control={<Checkbox checked={agreed} onChange={handleAgreeChange} />}
                                label="I agree to the End-User License Agreement"
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelClick} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleAgreeClick} color="primary" disabled={!agreed}>
                    Agree
                </Button>
            </DialogActions>

        </Dialog>
    );
};

export default EULAModal;